import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import AnnotationTypeDropdownInput from 'molbio/annotations/AnnotationTypeDropdownInput';
import IndexSelectionGroup from 'molbio/IndexSelectionGroup';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  MAX_AA_SEQUENCE_ANNOTATION_NAME_LENGTH,
  MAX_AA_SEQUENCE_ANNOTATION_NOTES_LENGTH,
} from 'toolbar/aa-sequence/AaSequenceAnnotationsPanelUtil';
import {
  AaSequenceAnnotationPanelState,
  aaSequenceAnnotationUIGlobalState,
} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import {isEnterKey} from 'util/Keyboard';

export interface CreateEditAaSequenceAnnotationFormProps {
  proteinId: string;
  aaSequenceLength: number;
  onClickSave: () => void;
  onClickCancel: () => void;
  isReadOnly: boolean;
  allAnnotationTypes: Array<string>;
}

/**
 * Form with inputs to create a new annotation or edit an existing one on a protein.
 */
function CreateEditAaSequenceAnnotationForm({
  proteinId,
  aaSequenceLength,
  onClickSave,
  onClickCancel,
  isReadOnly,
  allAnnotationTypes,
}: CreateEditAaSequenceAnnotationFormProps): JSX.Element {
  const [aaSequenceAnnotationPanelState, setAaSequenceAnnotationPanelState] =
    aaSequenceAnnotationUIGlobalState.useGlobalClientState(proteinId);
  const nameFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Sometimes we race with other less important focus actions, so setTimeout so we run last.
    setTimeout(() => {
      if (nameFieldRef.current) {
        nameFieldRef.current.focus();
      }
    }, 0);
  }, [nameFieldRef]);

  function trackEditField(event: string): void {
    analytics.track('protein', event, {product: ANALYTICS_MODULE.MOLBIO, protein_id: proteinId});
  }

  function updateFormState(data: Partial<AaSequenceAnnotationPanelState>): void {
    setAaSequenceAnnotationPanelState({...aaSequenceAnnotationPanelState, ...data});
  }

  return (
    <div
      className={css(styles.formContainer)}
      onKeyDown={(e) => {
        if (!isReadOnly && isEnterKey(e)) {
          onClickSave();
        }
      }}
      {...getTestProps({component: 'ProteinAnnotationEditor', element: 'root'})}
    >
      {aaSequenceAnnotationPanelState.isCreateActive && (
        <FieldLabel className={css(styles.newAnnotationTitle)} label="New annotation" />
      )}
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Name" />
        <TextInput
          className={css(styles.fieldInput)}
          placeholder="Annotation name"
          value={aaSequenceAnnotationPanelState.name}
          onChange={(e) => {
            updateFormState({name: e.target.value});
          }}
          onBlur={() => {
            trackEditField('edited_name');
          }}
          inputRef={nameFieldRef}
          disabled={isReadOnly}
          maxLength={MAX_AA_SEQUENCE_ANNOTATION_NAME_LENGTH}
          dataTestAttrs={{component: 'ProteinAnnotationEditor', element: 'input', key: 'annotationName'}}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Position" />
        <IndexSelectionGroup
          endIndex={aaSequenceAnnotationPanelState.end}
          sequenceLength={aaSequenceLength}
          sequenceStartIndex={1}
          startIndex={aaSequenceAnnotationPanelState.start}
          updateEndIndex={(endIndex: number) => {
            updateFormState({end: endIndex});
          }}
          updateStartIndex={(startIndex: number) => {
            updateFormState({start: startIndex});
          }}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Annotation type" />
        <AnnotationTypeDropdownInput
          className={css(styles.fieldInput)}
          type={aaSequenceAnnotationPanelState.annotationType}
          allTypes={allAnnotationTypes}
          onChangeType={(type: string): void => {
            updateFormState({annotationType: type});
          }}
          onBlur={() => {
            trackEditField('edited_annotation_type');
          }}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.fieldInputLabel)} label="Color" />
        <PopoverColorPicker
          className={css(styles.fieldInput)}
          selectedColor={aaSequenceAnnotationPanelState.color}
          onSelectColor={(color) => {
            updateFormState({color});
          }}
          asDropdown={true}
          disabled={isReadOnly}
        />
      </FormGroup>
      <FormGroup className={css(styles.fieldRow)}>
        <FieldLabel className={css(styles.notesLabel)} label="Notes" />
        <textarea
          className={css(styles.fieldInput)}
          placeholder="Notes"
          maxLength={MAX_AA_SEQUENCE_ANNOTATION_NOTES_LENGTH}
          value={aaSequenceAnnotationPanelState.notes}
          onChange={(e) => {
            updateFormState({notes: e.target.value});
          }}
          rows={aaSequenceAnnotationPanelState.notes ? 3 : 1}
          disabled={isReadOnly}
        />
      </FormGroup>
      <div className={css(styles.buttonRow)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={onClickCancel}
          text="Cancel"
        />
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={onClickSave}
          text="Save"
          disabled={isReadOnly}
          dataTestAttrs={{component: 'ProteinAnnotationEditor', element: 'button', key: 'save'}}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  newAnnotationTitle: {
    marginBottom: '16px',
  },
  formContainer: {
    padding: '12px',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingTop: '16px',
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  fieldInput: {
    width: '172px',
  },
  fieldInputLabel: {
    margin: 'auto 0',
  },
  notesLabel: {
    marginTop: 6,
  },
});

export default CreateEditAaSequenceAnnotationForm;
